/**
 * Chord inversion recognition — "Which inversion is this?"
 *
 * Plays a chord in a specified inversion. The user identifies the bass note's
 * relationship to the chord (root position, 1st inversion, 2nd inversion).
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import { buildChord } from "../utils.js";

export interface ChordInversionParams {
  quality: string;
  inversion: number; // 0, 1, 2
}

const INVERSION_NAMES = [
  "Root position",
  "First inversion",
  "Second inversion",
];

const INVERSION_DESC = [
  "root in the bass",
  "third in the bass",
  "fifth in the bass",
];

/** Apply an inversion: move the bottom `inv` notes up an octave. */
function applyInversion(chord: number[], inv: number): number[] {
  const result = [...chord];
  for (let i = 0; i < inv; i++) {
    result[i] = result[i]! + 12;
  }
  return result.sort((a, b) => a - b);
}

export function generateChordInversion(
  itemId: string,
  params: ChordInversionParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const rootMidi = 53 + rng.int(8); // F3..C4 area
  const baseChord = buildChord(rootMidi, params.quality);
  const inverted = applyInversion(baseChord, params.inversion);

  const builder = new AudioPlanBuilder();
  // Play arpeggio bottom-up so the bass note is clearly heard first.
  for (const note of inverted) {
    builder.addNote(note, 0.55, 0.20, "melody");
    builder.advance(0.30);
  }
  builder.advance(0.4);
  // Block chord twice for confirmation.
  builder.addChord(inverted, 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const options: AnswerOption[] = [0, 1, 2].map((inv) => ({
    id: `inv-${inv}`,
    label: INVERSION_NAMES[inv]!,
    sublabel: INVERSION_DESC[inv]!,
  }));

  return {
    itemId,
    exerciseType: "chord-inversion-recognition",
    audio,
    prompt: "Which inversion did you hear?",
    options,
    correctAnswerId: `inv-${params.inversion}`,
    meta: {
      seed,
      explanation: `${INVERSION_NAMES[params.inversion]} — ${INVERSION_DESC[params.inversion]} (${params.quality} chord).`,
      distractorCount: 2,
      teachAudio: buildInversionTeachPlan(inverted),
    },
  };
}

/**
 * Wrong-answer teach plan for chord inversion: arpeggiate the played
 * voicing slowly, then re-emphasize the bass note alone (the
 * inversion-defining tone), then the block chord. Inversions are
 * primarily heard via the bass — drawing extra attention to it after
 * the arpeggio is the highest-leverage cue.
 */
function buildInversionTeachPlan(inverted: number[]): AudioPlan {
  const bass = inverted[0]!;
  const builder = new AudioPlanBuilder();
  for (const note of inverted) {
    builder.addNote(note, 0.7, 0.22, "melody");
    builder.advance(0.45);
  }
  builder.advance(0.4);
  builder.addNote(bass, 1.4, 0.24, "target");
  builder.advance(1.6);
  builder.addChord(inverted, 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
